import type { Directive, DirectiveBinding } from 'vue'
import { watch, type WatchStopHandle } from 'vue'
import { usePermissionsStore } from '@/stores/permissions'

type PermissionBinding = string | string[]

interface PermissionElement extends HTMLElement {
  _permStop?: WatchStopHandle
}

function normalize(value: PermissionBinding): string[] {
  if (!value) {
    throw new Error('need permission! Like v-permission="\'user:create\'"')
  }
  return Array.isArray(value) ? value : [value]
}

function hasAnyPermission(required: string[]): boolean {
  const store = usePermissionsStore()
  return required.some((perm) => store.hasPermission(perm))
}

function applyVisibility(el: PermissionElement, required: string[]): void {
  el.hidden = !hasAnyPermission(required)
}

/**
 * v-permission directive — hides elements when the user lacks permission.
 * Re-evaluates when the permissions store updates (async RBAC load).
 *
 * Usage:
 * <Button v-permission="PERMISSIONS.INVOICES_CREATE">...</Button>
 * <div v-permission="['user:create', 'user:update']">...</div> (visible if ANY)
 */
export const permission: Directive<PermissionElement, PermissionBinding> = {
  mounted(el, binding: DirectiveBinding<PermissionBinding>) {
    const required = normalize(binding.value)
    applyVisibility(el, required)
    el._permStop = watch(
      () => usePermissionsStore().permissions,
      () => applyVisibility(el, required),
      { deep: true },
    )
  },

  updated(el, binding: DirectiveBinding<PermissionBinding>) {
    applyVisibility(el, normalize(binding.value))
  },

  unmounted(el) {
    el._permStop?.()
    delete el._permStop
  },
}
